import React, { ComponentType, VFC } from 'react';

import { ThemeDefinition } from '@tanium/react-theme-definition';

import { useChartColorScale } from '../../hooks/useChartColorScale';
import { ColorScale } from '../../utils/ColorScale';
import { defaultPalette } from '../../utils/defaultPalette';
import { ExtendAndOverride } from '../../utils/ExtendAndOverride';
import { StringCoercible } from '../../utils/StringCoercible';

/**
 * Props that apply to charts that support color assignments.
 */
export interface ChartColorProps<TDataKeyType> {
  /**
   * A custom color scale to use to determine the color of each item in the chart. If not specified,
   * a default color scale will be used with the specified palette (or a default palette).
   */
  colorScale?: ColorScale<TDataKeyType>;
  /**
   * A custom palette to use as the set of assignable colors for items in the chart. This palette is
   * only used if a color scale is not specified; otherwise, this prop has no effect.
   */
  palette?: readonly ThemeDefinition[];
}

/**
 * Props injected into a component wrapped by `withColorScale`.
 */
export interface ColorScaleInjectedProps<TDataKeyType> {
  colorScale: ColorScale<TDataKeyType>;
  // `never` reflects that the `palette` prop is explicitly omitted and not passed through
  palette?: never;
}

type WithColorScaleHelperProps<TInnerProps, TDataKeyType> = ExtendAndOverride<
  ChartColorProps<TDataKeyType>,
  TInnerProps
>;

export const withColorScale = <
  TInnerProps extends ColorScaleInjectedProps<TDataKeyType>,
  TDataKeyType = TInnerProps['colorScale'] extends ColorScale<infer U> ? U : StringCoercible,
>(
  InnerComponent: React.ComponentType<TInnerProps>,
): ComponentType<WithColorScaleHelperProps<TInnerProps, TDataKeyType>> => {
  const ColorScaleHelper: VFC<WithColorScaleHelperProps<TInnerProps, TDataKeyType>> = ({
    colorScale: colorScaleProp,
    palette = defaultPalette,
    ...props
  }) => {
    const colorScale = useChartColorScale(palette, colorScaleProp);

    const innerProps = { ...props, colorScale };

    return <InnerComponent {...(innerProps as unknown as TInnerProps)} />;
  };

  ColorScaleHelper.displayName = `WithColorScale(${InnerComponent.displayName ?? 'Component'})`;

  return ColorScaleHelper;
};
